import { LoadingCommon } from '@components';
import { Button, Tooltip } from 'antd';
import { PhotoEnlargeDialog } from './components/PhotoEnlargeDialog';
import { ASSET_PHOTO_CONSTANT } from './constant';
import useAssetPhotoInquiry from './hooks/useAssetPhotoInquiry';
import {
  AssetInfoPanel,
  NoDataText,
  PhotoCountBadge,
  PhotoWrapper,
  ThumbnailCard,
  ThumbnailDate,
  ThumbnailGrid,
  ThumbnailImage,
  ThumbnailImageError,
} from './AssetPhotoInquiryStyled';
import { useState } from 'react';

const { TITLE, LABEL, MESSAGE } = ASSET_PHOTO_CONSTANT;

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '—';
  }
  const datePart = dateStr.split('T')[0]; // strip time from ISO 8601 if present
  const parts = datePart.split('-');
  if (parts.length !== 3) {
    return dateStr;
  }
  return `${parts[0]}/${parts[1]}/${parts[2]}`;
}

interface ThumbnailCardItemProps {
  src: string;
  takenAt: string | null;
  onClick: () => void;
}

function ThumbnailCardItem({ src, takenAt, onClick }: ThumbnailCardItemProps) {
  const [imgError, setImgError] = useState(false);

  return (
    <ThumbnailCard onClick={onClick}>
      {imgError ? (
        <Tooltip title={MESSAGE.E02}>
          <ThumbnailImageError>
            <span className="thumbnail_error-icon">🖼</span>
            <span>{MESSAGE.E02}</span>
          </ThumbnailImageError>
        </Tooltip>
      ) : (
        <ThumbnailImage
          src={src}
          alt={takenAt ?? ''}
          onError={() => setImgError(true)}
        />
      )}
      <ThumbnailDate>{formatDate(takenAt)}</ThumbnailDate>
    </ThumbnailCard>
  );
}

function AssetPhotoInquiry() {
  const { state, handler } = useAssetPhotoInquiry();
  const { assetInfo, photos, isLoading, selectedPhotoIndex, isDialogOpen } = state;

  return (
    <LoadingCommon loading={isLoading}>
      <PhotoWrapper>
        {/* [3] Header: back button left, title centered */}
        <div className="photo_header">
          <button className="photo_back-btn" onClick={handler.handleBack}>
            {TITLE.BTN_BACK}
          </button>
          <h1>{TITLE.PAGE}</h1>
        </div>

        {/* [4] Asset info area — read-only 2×2 grid */}
        <div className="photo_section">
          <div className="photo_section-title">{LABEL.ASSET_INFO_SECTION}</div>
          <AssetInfoPanel>
            <div className="asset-info_item">
              <span className="asset-info_label">{LABEL.ASSET_NO}</span>
              <span className="asset-info_value">{assetInfo?.asset_no || '—'}</span>
            </div>
            <div className="asset-info_item">
              <span className="asset-info_label">{LABEL.ASSET_NAME}</span>
              <span className="asset-info_value">{assetInfo?.asset_name || '—'}</span>
            </div>
            <div className="asset-info_item">
              <span className="asset-info_label">{LABEL.COMPANY}</span>
              <span className="asset-info_value">
                {assetInfo && (assetInfo.company_code || assetInfo.company_name)
                  ? [assetInfo.company_code, assetInfo.company_name].filter(Boolean).join('_')
                  : '—'}
              </span>
            </div>
            <div className="asset-info_item">
              <span className="asset-info_label">{LABEL.STORE}</span>
              <span className="asset-info_value">
                {assetInfo && (assetInfo.store_code || assetInfo.store_name)
                  ? `${assetInfo.store_code} ${assetInfo.store_name}`.trim()
                  : '—'}
              </span>
            </div>
          </AssetInfoPanel>
        </div>

        {/* [5] Photo count + [6] Thumbnail grid */}
        <div className="photo_section">
          {/* [5] 写真件数 — top-right */}
          <div className="photo_thumbnail-header">
            <PhotoCountBadge>
              <strong>{photos.length}</strong>
              {TITLE.PHOTO_COUNT_UNIT}
            </PhotoCountBadge>
          </div>

          {/* [6] Thumbnail grid */}
          {photos.length === 0 ? (
            <NoDataText>{MESSAGE.I01}</NoDataText>
          ) : (
            <ThumbnailGrid>
              {photos.map((photo, index) => (
                <ThumbnailCardItem
                  key={photo.id}
                  src={photo.file_path}
                  takenAt={photo.taken_at}
                  onClick={() => handler.handleThumbnailClick(index)}
                />
              ))}
            </ThumbnailGrid>
          )}
        </div>

        {/* [7] Footer back button */}
        <div className="photo_footer">
          <Button size="large" onClick={handler.handleBack}>
            {TITLE.BTN_BACK_LABEL}
          </Button>
        </div>

        {/* VAMS_PC_006_01 Photo Enlarge Dialog */}
        <PhotoEnlargeDialog
          open={isDialogOpen}
          photos={photos}
          currentIndex={selectedPhotoIndex}
          assetInfo={assetInfo}
          onClose={handler.handleDialogClose}
          onPrev={handler.handlePrevPhoto}
          onNext={handler.handleNextPhoto}
        />
      </PhotoWrapper>
    </LoadingCommon>
  );
}

export { AssetPhotoInquiry };
